import { memo } from "react";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Today } from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { weekDaysOf, type DateFilterMode } from "../helpers/dateRange";
import type { TeleconsultoriaDateRange } from "../hooks/useTeleconsultoriaDateRange";

function periodLabel(range: TeleconsultoriaDateRange): string {
  const localized = range.date.locale("pt-br");
  if (range.mode === "month") return localized.format("MMMM [de] YYYY");
  if (range.date.isSame(dayjs(), "day")) return `Hoje · ${localized.format("DD/MM")}`;
  return localized.format("ddd, DD/MM/YYYY");
}

/**
 * Filtro de datas da Teleconsultoria (Dia/Mês, como o ScheduleManage):
 * seletor Dia/Mês + setas + "Hoje" e, no modo dia, a tira da semana para
 * trocar de dia com um toque.
 */
export const TeleconsultoriaDateFilter = memo(function TeleconsultoriaDateFilter({
  range,
}: {
  range: TeleconsultoriaDateRange;
}) {
  const today = dayjs();
  const stepLabel = range.mode === "day" ? "dia" : "mês";

  return (
    <Stack spacing={1} width="100%">
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={range.mode}
          onChange={(_, next: DateFilterMode | null) => next && range.setMode(next)}
          aria-label="Período"
        >
          <ToggleButton value="day" sx={{ textTransform: "none", px: 1.5 }}>Dia</ToggleButton>
          <ToggleButton value="month" sx={{ textTransform: "none", px: 1.5 }}>Mês</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <IconButton size="small" onClick={range.goPrevious} aria-label={`${stepLabel} anterior`}>
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            noWrap
            sx={{ textTransform: "capitalize", minWidth: 0, textAlign: "center", flex: 1 }}
          >
            {periodLabel(range)}
          </Typography>
          <IconButton size="small" onClick={range.goNext} aria-label={`Próximo ${stepLabel}`}>
            <ChevronRight />
          </IconButton>
        </Stack>
        <Tooltip title="Voltar para hoje">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Today fontSize="small" />}
              onClick={range.goToday}
              disabled={range.isCurrentPeriod}
              sx={{ textTransform: "none" }}
            >
              Hoje
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {range.mode === "day" && (
        <Box
          role="listbox"
          aria-label="Dias da semana"
          sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(40px, 1fr))", gap: 0.5 }}
        >
          {weekDaysOf(range.date).map((day) => {
            const isSelected = day.isSame(range.date, "day");
            const isToday = day.isSame(today, "day");
            return (
              <ButtonBase
                key={day.format("YYYY-MM-DD")}
                role="option"
                aria-selected={isSelected}
                onClick={() => range.setDate(day)}
                sx={{
                  flexDirection: "column",
                  py: 0.75,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : isToday ? "primary.light" : "divider",
                  bgcolor: isSelected ? "primary.main" : "transparent",
                  color: isSelected ? "primary.contrastText" : "text.primary",
                }}
              >
                <Typography variant="caption" sx={{ textTransform: "capitalize", lineHeight: 1.2 }}>
                  {day.locale("pt-br").format("ddd").replace(".", "")}
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                  {day.format("DD")}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      )}
    </Stack>
  );
});
